import { useState, useEffect } from "react";
import { toast } from "sonner";
import { fetchAuthSession } from "aws-amplify/auth";
import { useLoading } from "../context/LoadingContext";
import { RegulifyService } from "../api/RegulifyService";
import { usePDDSelectedDeviceStore } from "../store/useDeviceCodeSelectionStore";
// import { useDeviceStore } from "../store/useDeviceSelectionFromLocator";
import { buildWsUrl } from "../utils/wsUrl";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE =
  "There was a problem processing your device pre-submission draft. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_CREATE_PRESUB_URL = (token) =>  buildWsUrl("create_fda_presubmission_document", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

const DevicePreSubmission = () => {

  // const selectedDevices = useDeviceStore((state) => state.selectedDevices); from locator global stored value
  // return (
  //   <div>
  //     <h2>Selected Devices</h2>
  //     <pre>{JSON.stringify(selectedDevices, null, 2)}</pre>
  //   </div>
  // );from locator global stored value

  const [formData, setFormData] = useState({
    sponsor: "",
    contact: "",
    email: "",
    phone: "",
    subject: "",
    deviceName: "",
    deviceDescription: "",
    intendedUse: "",
    alphanumericList: [""],
  });
  const { setLoading } = useLoading();
  const [error, setError] = useState("");

  const selectedDevices = usePDDSelectedDeviceStore(
    (state) => state.selectedDevices
  );


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleListChange = (index, value) => {
    const newList = [...formData.alphanumericList];
    newList[index] = value;
    setFormData((prev) => ({ ...prev, alphanumericList: newList }));
  };

  const addListItem = () => {
    if (formData.alphanumericList.length >= 2) return;

    setFormData((prev) => ({
      ...prev,
      alphanumericList: [...prev.alphanumericList, ""],
    }));
  };


  const removeListItem = (index) => {
    const newList = formData.alphanumericList.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, alphanumericList: newList }));
  };

  function handleParsedMessage(parsed, { setLoading }) {
    switch (parsed.type) {
      case "error":
        console.error("Processing error:", parsed.message);
        toast.error(`Error: ${parsed.message}`);
        setLoading(false);
        return true;

      case "info":
        console.info("Processing update:", parsed.message);
        toast.info(`Update: ${parsed.message}`);
        return true;

      case "pong":
        console.info("Received ping/pong reply:", parsed.message);
        return true;

      default:
        return false;
    }
  }

  const handlePreSubCreation = async () => {
    for (const [key, val] of Object.entries(formData)) {
      if (Array.isArray(val)) {
        if (!val.length || val.some((v) => !v.trim())) {
          setError("All fields are mandatory.");
          return;
        }
      } else if (!val.trim()) {
        setError("All fields are mandatory.");
        return;
      }
    }

    setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

    let keepAliveInterval;

    try {
      await RegulifyService.get_allowed_credits(1);

      const token = await getAuthToken();

      const ws = new WebSocket(WS_CREATE_PRESUB_URL(token));
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          sponsor: formData["sponsor"],
          contact: formData["contact"],
          email: formData["email"],
          phone: formData["phone"],
          subject: formData["subject"],
          deviceName: formData["deviceName"],
          deviceDescription: formData["deviceDescription"],
          intendedUse: formData["intendedUse"],
          alphanumericList: formData["alphanumericList"],
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed.content && parsed.filename) {
            const byteCharacters = atob(parsed.content);
            const byteArray = new Uint8Array(
              [...byteCharacters].map((c) => c.charCodeAt(0))
            );

            const blob = new Blob([byteArray], {
              type:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = parsed.filename;
            link.click();
          } else {
            console.log("Unexpected message format:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error.message);
      setLoading(false);
    }

  };

  useEffect(() => {
    if (selectedDevices.length > 0) {
      const knumbers = selectedDevices
        .map((d) => d.knumber)
        .filter(Boolean)
        .slice(0, 2);

      setFormData((prev) => ({
        ...prev,
        alphanumericList:
          knumbers.length > 0 ? knumbers : [""],
      }));
    }
  }, [selectedDevices]);

  return (
    <div className="bg-base-100 flex h-screen w-full flex-col p-4 md:p-6">
      <h1 className="mb-2 flex-shrink-0 text-center text-2xl font-semibold md:text-3xl">
        Device Pre-Submission Form
      </h1>

      {/* Form container */}
      <div className="flex flex-1 gap-6 overflow-auto">
        {/* Personal Information Section */}
        <section className="bg-base-100 border-base-300 flex-1 overflow-auto rounded-lg border p-4 shadow-inner md:p-6">
          <h2 className="border-base-300 mb-1 flex-shrink-0 border-b pb-1 text-lg font-semibold md:text-xl">
            Personal Information
          </h2>

          <div className="grid grid-cols-1 gap-y-3">
            {["sponsor", "contact", "email", "phone", "subject"].map(
              (field) => (
                <div
                  key={field}
                  className="flex flex-col gap-2 md:flex-row md:items-center"
                >
                  <label className="w-full text-sm font-medium capitalize md:w-40 md:text-base">
                    {field}
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className="input input-bordered w-full flex-1 text-sm md:text-base"
                    required
                  />
                </div>
              ),
            )}
          </div>
          {/* Submit Button pinned at bottom */}
          <div className="mt-4 flex flex-col items-center justify-center">
            {error && <p className="text-error mb-2 text-center">{error}</p>}
            <button
              type="submit"
              onClick={handlePreSubCreation}
              className={`btn btn-primary tooltip tooltip-center w-half md:w-1/2}`}
              data-tip="Click on it to start pre-submission letter draft creation process for provided device input and choosen predicates"
            >
              Generate PreSubmission Draft
            </button>
          </div>
        </section>

        {/* Device Information Section */}
        <section className="bg-base-100 border-base-300 flex-1 overflow-auto rounded-lg border p-4 shadow-inner md:p-6">
          <h2 className="border-base-300 mb-1 flex-shrink-0 border-b pb-1 text-lg font-semibold md:text-xl">
            Device Information
          </h2>

          <div className="grid grid-cols-1 gap-y-3">
            {/* Device Name */}
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
              <label className="w-full text-sm font-medium md:w-40 md:text-base">
                Device Name
              </label>
              <input
                type="text"
                name="deviceName"
                value={formData.deviceName}
                onChange={handleChange}
                className="input input-bordered w-full flex-1 text-sm md:text-base"
                required
              />
            </div>

            {/* Device Description */}
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
              <label className="w-full text-sm font-medium md:w-40 md:text-base">
                Device Description (Include device principle operations)
              </label>
              <textarea
                name="deviceDescription"
                value={formData.deviceDescription}
                onChange={handleChange}
                className="textarea textarea-bordered w-full flex-1 text-sm md:text-base"
                rows="4"
                required
              ></textarea>
            </div>

            {/* Intended Use / Indications */}
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
              <label className="w-full text-sm font-medium md:w-40 md:text-base">
                Intended Use / Indications
              </label>
              <textarea
                name="intendedUse"
                value={formData.intendedUse}
                onChange={handleChange}
                className="textarea textarea-bordered w-full flex-1 text-sm md:text-base"
                rows="4"
                required
              ></textarea>
            </div>

            {/* Predicate device 510k List */}
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
              <label className="w-full text-sm font-medium md:w-40 md:text-base">
                Predicate 510K Numbers
              </label>
              <div className="flex flex-1 flex-col gap-2">
                {selectedDevices.length > 0 && (
                  <div className="text-xs text-success">
                    Auto-populated from selected devices
                  </div>
                )}
                {formData.alphanumericList.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleListChange(index, e.target.value)}
                      className="input input-bordered w-full flex-1 text-sm md:text-base"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeListItem(index)}
                      className="btn btn-error btn-sm"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addListItem}
                  disabled={
                    formData.alphanumericList.length >= 2
                  }
                  className="btn btn-outline btn-sm mt-2 w-max"
                >
                  + Add Predicate
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
export default DevicePreSubmission;
